<template>
  <div>
    <settings-section-title id="create" class="i-mb-4"
      >Create API Key</settings-section-title
    >
    <breadcrumbs :items="breadcrumbs"></breadcrumbs>

    <div
      v-if="hasEditPermissions"
      class="d-flex flex-column i-gap-6 i-mt-12 create-form"
    >
      <!-- name -->
      <i-text-input
        :value="keyName"
        placeholder="Enter a name for the API Key"
        :has-error="keyHasError"
        :show-hint-text="true"
        label="Name"
        :hint-text="
          !keyName || keyName === ''
            ? 'The API Key name field is required'
            : keyName.length > 40
              ? 'The API Key Name has a maximum length limit of 40 characters'
              : keyNameExists
                ? 'The API Key Name must be unique, another key with this name exists'
                : null
        "
        @input="keyName = $event"
      ></i-text-input>

      <!-- description -->
      <i-text-input
        label="Description (optional)"
        :value="keyDescription"
        :has-error="descriptionHasError"
        :show-hint-text="true"
        :hint-text="
          descriptionHasError
            ? 'The API Key Description field has a maximum length limit of 150 characters'
            : null
        "
        :mode="iTextInputModes.TEXTAREA"
        @input="keyDescription = $event"
      ></i-text-input>

      <i-button
        text="Create"
        class="i-mt-6"
        size="lg"
        :disabled="loading || hasErrors"
        @button-click="createKey()"
      ></i-button>
    </div>

    <i-modal
      modal-id="copy-key-modal"
      title="API Key Created"
      subtitle="Please copy your API key and store it in a safe place"
      confirm-button-text="Close"
      type="primary"
      @on-submit="closeModal"
    >
      <template #body-content>
        <div v-if="newApiToken">
          <label class="i-mb-4 i-text-subheading-2 i-text-neutral-120"
            >Secret</label
          >

          <i-copy-input
            :value="newApiToken.full_token"
            @text-copied="onTextCopied"
          ></i-copy-input>

          <p class="i-text-body-3 i-text-neutral-110 i-mt-4">
            Please copy this now as we cannot show it again
          </p>
        </div>
      </template>
    </i-modal>
  </div>
</template>

<script>
import { mapGetters, mapState, mapActions, mapMutations } from 'vuex';
import {
  iButton,
  iCopyInput,
  iTextInput,
  iTextInputModes,
  iModal,
  openModal,
  showToast,
} from '@inscribe/signal';
import Breadcrumbs from '@/shared/components/Breadcrumbs';
import SettingsSectionTitle from '@/V2/settings/shared/SettingsSectionTitle';
import { GENERATE_API_KEY } from '@/constants/onboardingChecklist';

export default {
  components: {
    Breadcrumbs,
    iCopyInput,
    iTextInput,
    iButton,
    iModal,
    'settings-section-title': SettingsSectionTitle,
  },
  data() {
    return {
      breadcrumbs: [
        {
          text: 'Settings',
          to: { name: 'settings-view' },
        },
        {
          text: 'API Keys',
          to: { name: 'api-settings-list-keys' },
        },
        {
          text: 'Create API Key',
          active: true,
        },
      ],
      keyName: '',
      keyDescription: '',
      keyCreationMessage: '',
      loading: false,
      newApiToken: null,
      existingKeyNames: [],
      iTextInputModes,
    };
  },
  computed: {
    ...mapGetters({
      hasEditPermissions: 'isOrgAdmin',
    }),

    ...mapState(['currentUser']),

    hasErrors() {
      return this.keyHasError || this.descriptionHasError;
    },

    keyHasError() {
      return (
        !this.keyName ||
        this.keyName === '' ||
        this.keyName.length > 40 ||
        this.keyNameExists
      );
    },

    descriptionHasError() {
      return this.keyDescription?.length > 150;
    },

    keyNameExists() {
      return (
        this.existingKeyNames && this.existingKeyNames.includes(this.keyName)
      );
    },
  },
  mounted() {
    this.existingKeyNames = this.currentUser.organisation.api_keys.reduce(
      (results, apiKeys) => {
        if (apiKeys.name) {
          results.push(apiKeys.name);
        }
        return results;
      },
      [],
    );
  },
  methods: {
    ...mapActions(['generateApiKey']),

    ...mapMutations(['addCurrentUser', 'addCompletedAction']),

    onTextCopied() {
      showToast({
        text: 'API Key copied',
      });
    },

    createKey() {
      this.loading = true;

      this.generateApiKey({
        name: this.keyName,
        description: this.keyDescription,
      })
        .then((response) => {
          this.loading = false;

          const currentUserCopy = {
            ...this.currentUser,
          };

          currentUserCopy.organisation.api_keys = [
            response.data,
            ...currentUserCopy.organisation.api_keys,
          ];

          this.newApiToken = response.data;
          this.keyCreationMessage = response.data.message;

          openModal('copy-key-modal');

          // mutations
          this.addCurrentUser(currentUserCopy);
          this.addCompletedAction(GENERATE_API_KEY);
        })
        .catch((error) => {
          if (error.response) {
            showToast({
              text: 'Error creating API Key',
              isError: true,
            });
            this.loading = false;
          }
        });
    },
    closeModal() {
      this.newApiToken = null;
      showToast({
        text: this.keyCreationMessage,
      });
      this.$router
        .push({
          name: 'api-settings-list-keys',
        })
        .catch(() => {});
    },
  },
};
</script>

<style lang="scss" scoped>
.create-form {
  max-width: 500px;
}
</style>
